// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { citecApi } from '@advisor-app/api/engine/citec-api';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '@advisor-app/store/store';
import {
  selectScreenersLoading,
  selectScreenersPositions,
  setScreeners,
} from '@advisor-app/store/slices/my-research';
import {
  updateOptimizationConfig,
  updateUniverseFilters,
} from '@advisor-app/store/slices/parameters';
import { useDeleteItemPopup } from './use-delete-item-popup';
import { useCallback, useState } from 'react';
import { type Screener } from '@shared/data-access/interfaces/redux/my-research-state';
import { exportUniversesToExcel } from '../utils/export-universes-to-excel';
import { useTranslation } from 'react-i18next';

export const useHandlerMyScreenerTable = (id: string) => {
  const dataScreenersTable = useAppSelector(selectScreenersPositions);
  const isLoadingScreeners = useAppSelector(selectScreenersLoading);
  const [isDownloading, setIsDownloading] = useState(false);
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const { deleteItemPopup } = useDeleteItemPopup();

  const handleDeleteScreener = async (universe_id: number, name: string) => {
    deleteItemPopup(
      {
        title: t('research.delete-screener.title', { ns: 'research' }),
        text: t('research.delete-screener.text', { name, ns: 'research' }),
        itemType: 'screener',
        params: {
          universe_id,
        },
        endpoint: '/citec/universe',
      },
      isDeleted => {
        /* istanbul ignore next */
        if (!isDeleted) return;
        const filteredScreeners = dataScreenersTable.filter(
          item => item.universe_id !== universe_id,
        );

        dispatch(setScreeners(filteredScreeners));
      },
    );
  };

  const loadScreener =
    (onComplete: VoidFunction) => async (universe_id: number) => {
      const data = await citecApi.getUniverse({ universe_id });

      const {
        sectors_filter,
        factors_filter,
        esg_exclusions,
        esg_scoring,
        esg_not_informed,
        to_exclude,
        to_include,
        universe_base,
      } = data;

      const formattedFactors = factors_filter.factor.map((factor, i) => ({
        factor,
        ranges: [factors_filter.lower[i] * 100, factors_filter.upper[i] * 100],
      }));

      const formattedEsgScoring = esg_scoring?.filter.map((factor, idx) => {
        if (factor === 'co2_emissions') {
          return {
            factor,
            ranges: [
              esg_scoring.lower[idx] / 5000000,
              esg_scoring.upper[idx] / 5000000,
            ],
          };
        } else {
          return {
            factor,
            ranges: [esg_scoring.lower[idx], esg_scoring.upper[idx]],
          };
        }
      });
      dispatch(
        updateUniverseFilters([
          id,
          {
            sectors_filter,
            factors_filter: formattedFactors,
            esg_exclusions,
            esg_scoring: formattedEsgScoring,
            esg_not_informed,
            to_exclude,
            to_include,
            universe_base: universe_base.description,
          },
        ]),
      );

      /* istanbul ignore if */
      if (universe_base.ticker) {
        dispatch(
          updateOptimizationConfig([
            id,
            { benchmark: [{ ticker: universe_base.ticker, weight: 1 }] },
          ]),
        );
      }

      return onComplete();
    };

  const onDownloadScreener = useCallback((data: Screener[]) => {
    setIsDownloading(true);

    // TODO: migrate to RTK Query
    exportUniversesToExcel(data, citecApi.getUniverse).then(() => {
      setIsDownloading(false);
    });
  }, []);

  return {
    dataScreenersTable,
    handleDeleteScreener,
    loadScreener,
    isLoadingScreeners,
    onDownloadScreener,
    isDownloading,
  };
};
